import {
  HeadlessAutoCompleteValueType,
  useAutoCompleteProvider,
  ValueResolver,
} from '@/components/vision/next/inputs/useAutoComplete'
import { computed, reactive, Ref, watch } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import User from '@/models/user'
import { BusinessUnit } from '@/models/business-unit'
import { PickerItemType } from '@/components/vision/next/picker-list/models'

type JobFollowersPickerOptions = {
  onNavigate?: () => void
  scrollToPosition?: (pos: number) => void
  valueResolver?: ValueResolver
  jobHubId: Ref<string>
  onValueChanged?: (items: HeadlessAutoCompleteValueType[]) => void
  value: Ref<string[] | null>
}

export interface SearchFollowerOptions {
  searchText?: string | null
  jobHubId: string
  value: string[] | null | undefined
}

type State = {
  searchText: string
}

const rolesNotAllowedToFollowJob = [
  'Member',
  'Operator',
  'Form Reviewer',
  'Analytics Viewer',
]

export const useJobFollowersPicker = (options: JobFollowersPickerOptions) => {
  const current = reactive<State>({
    searchText: '',
  })
  const search = computed(() => current.searchText)
  const autoComplete = useAutoCompleteProvider({
    search,
    onValueChanged: options.onValueChanged as any,
    value: options.value as any,
    valueResolver: options.valueResolver,
    multiple: true,
  })

  const searchOptions = computed<SearchFollowerOptions>(() => ({
    searchText: current.searchText,
    jobHubId: options.jobHubId.value,
    value: options.value.value,
  }))

  const { items, total } = useSearchJobFollower(searchOptions)

  // if the user is actively searching or changing
  // filters we should request to reset the scroll position
  watch(searchOptions, (newOpts, oldOpts) => {
    if (oldOpts.searchText !== newOpts.searchText) {
      options.scrollToPosition?.(0)
    }
  })

  const selectItem = (item: PickerItemType) => {
    autoComplete.toggleValue(item)
  }

  const updateSearch = (newSearch: string) => {
    current.searchText = newSearch
  }

  const clear = () => {
    autoComplete.clear()
    current.searchText = ''
    options.scrollToPosition?.(0)
  }

  const noResultsMessage = computed(() => {
    if (items.value.length > 0) {
      return
    }
    return 'No results'
  })

  const clearSearch = () => {
    current.searchText = ''
  }

  return {
    items,
    total,
    updateSearch,
    selectItem,
    clear,
    clearSearch,
    noResultsMessage,
  }
}

const useSearchJobFollower = (options: Ref<SearchFollowerOptions>) => {
  const store = useStore()
  const userBySid = computed<(userSid: string) => User | null | undefined>(
    () => store.getters['users/getBySid']
  )
  const getUsersRelatedToBusinessUnit = computed<(hubId: string) => User[]>(
    () => store.getters['users/getUsersRelatedToBusinessUnit']
  )
  const users = computed(() =>
    getUsersRelatedToBusinessUnit.value(options.value.jobHubId)
  )
  const getBusinessUnitById = computed<
    (uuid: string | null) => BusinessUnit | null | undefined
  >(() => store.getters['businessUnits/getBusinessUnitById'])

  const usersArray = computed(() => {
    if (!options.value.value?.length) {
      return users.value.filter((u) => {
        return u.roles.some(
          (role) => !rolesNotAllowedToFollowJob.includes(role)
        )
      })
    }

    const selectedDisabledUsersIds = options.value.value.filter(
      (s) => !users.value.map((u) => u.id?.toString()).includes(s)
    )

    const selectedDisabledUsers = selectedDisabledUsersIds
      .map((u) => userBySid.value(u))
      // Filter out null and undefined type in an array - For TypeScript type checking only
      // filter(Boolean) also works, but TypeScript will still think the array contains null and undefined
      .filter((x): x is User => !!x)

    return users.value.concat(selectedDisabledUsers).filter((u) => {
      return u.roles.some((role) => !rolesNotAllowedToFollowJob.includes(role))
    })
  })

  const total = computed(() => usersArray.value.length)

  const items = computed<Readonly<PickerItemType[]>>(() =>
    usersArray.value
      .map((c) => ({
        id: c.id,
        title: c.name ?? '',
        subTitle: getBusinessUnitById.value(c.organizationId)?.name ?? null,
        selectable: true,
      }))
      .filter((c) =>
        c.title
          .toLowerCase()
          .includes(options.value.searchText?.toLowerCase() ?? '')
      )
  )
  return { items, total }
}
